import React from 'react';
import { Link } from 'react-router-dom';
import { GroupBuy } from '../../types';
import { toImageUrl } from '../../utils/image';
import CountdownTimer from './CountdownTimer';

const STATUS_LABEL: Record<string, { text: string; color: string }> = {
  COLLECTING: { text: 'Collecting', color: '#b39ddb' },
  CLOSED: { text: 'Closed', color: '#888' },
  PURCHASING: { text: 'Purchasing', color: '#fb8c00' },
  SHIPPING: { text: 'Shipping', color: '#1976d2' },
  DONE: { text: 'Done', color: '#4caf50' },
  FAILED: { text: 'Failed', color: '#e53935' },
};

interface Props {
  groupBuy: GroupBuy;
}

const GroupBuyCard: React.FC<Props> = ({ groupBuy }) => {
  const isUpcoming = groupBuy.phase === 'UPCOMING';
  const isCollecting = groupBuy.phase === 'COLLECTING';
  const st = isUpcoming
    ? { text: 'Upcoming', color: '#9575cd' }
    : (STATUS_LABEL[groupBuy.status] ?? { text: groupBuy.status, color: '#888' });

  return (
    <Link to={`/group-buys/${groupBuy.code}`} style={styles.card}>
      <div style={styles.imageWrap}>
        <img
          src={toImageUrl(groupBuy.thumbnailUrl ?? '')}
          alt={groupBuy.title}
          style={styles.image}
        />
        <span style={{ ...styles.statusBadge, background: st.color }}>{st.text}</span>
      </div>
      <div style={styles.info}>
        {groupBuy.webtoonTitle && <p style={styles.webtoon}>{groupBuy.webtoonTitle}</p>}
        <p style={styles.title}>{groupBuy.title}</p>
        {groupBuy.artist && <p style={styles.artist}>by {groupBuy.artist}</p>}
        <p style={styles.price}>${Number(groupBuy.priceUsd).toFixed(2)}</p>

        {isCollecting && (
          <div style={styles.countdown}>
            <CountdownTimer deadline={groupBuy.deadline} />
          </div>
        )}

        {isUpcoming && groupBuy.startAt && (
          <p style={styles.opensAt}>🗓 Opens {new Date(groupBuy.startAt).toLocaleDateString()}</p>
        )}

        {(() => {
          const remaining = Math.max(0, (groupBuy.stock ?? 0) - (groupBuy.currentQuantity ?? 0));
          return remaining <= 0 ? (
            <div style={{ marginTop: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#e53935' }}>Sold out</span>
            </div>
          ) : null;
        })()}

        {groupBuy.estimatedShippingWeeks && (
          <p style={styles.shipping}>🚢 Ships in {groupBuy.estimatedShippingWeeks}</p>
        )}
      </div>
    </Link>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: { display: 'block', borderRadius: 10, overflow: 'hidden', background: '#fff', border: '1px solid #f0f0f0', cursor: 'pointer', transition: 'box-shadow 0.2s' },
  imageWrap: { position: 'relative', paddingBottom: '100%', overflow: 'hidden', background: '#f5f5f5' },
  image: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  statusBadge: { position: 'absolute', top: 8, left: 8, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12 },
  info: { padding: '12px 14px 16px' },
  webtoon: { fontSize: 11, color: '#b39ddb', fontWeight: 600, marginBottom: 2 },
  title: { fontSize: 14, fontWeight: 700, color: '#333', marginBottom: 4, lineHeight: 1.4 },
  artist: { fontSize: 11, color: '#aaa', marginBottom: 6 },
  price: { fontSize: 16, fontWeight: 800, color: '#333', marginBottom: 8 },
  countdown: { marginBottom: 8 },
  opensAt: { fontSize: 12, color: '#9575cd', fontWeight: 600, marginBottom: 8 },
  shipping: { fontSize: 11, color: '#888', marginTop: 8 },
};

export default GroupBuyCard;
